import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { getGroupedSelectedShippingMethodsWithProducts } from '../../../slices/shippingMethods/shippingMethods.utils';
import PrimaryButton from '../../../components/common/PrimaryButton';
import DeliveryMethodCard from '../../../components/forms/DeliveryMethodsStep/DeliveryMethodCard';
import DeliveryMethodsStepInfo from '../DeliveryMethodsStepInfo';
import styles from './DeliveryMethodsStep.module.scss';
import messages from './messages';
import commonMessages from '../messages';
import ErrorBanner from '../../../components/common/ErrorBanner';
import useSteppedCheckoutError from '../../../hooks/useSteppedCheckoutError';
import Spinner, {
  THEME_BLOCK_PURPLE,
} from '../../../components/common/Spinner';
import SecondaryButton from '../../../components/common/SecondaryButton';
import {
  DELIVERY_METHODS_SMALL_PRODUCT,
  DELIVERY_METHODS_LARGE_PRODUCT,
} from './constants';
import useSlaService, {
  SLA_PRODUCT_SOURCE,
} from '../../../hooks/useSlaService';

const DeliveryMethodsStep = ({
  showCompleted,
  isCompleted,
  selectedShippingMethods,
  onSelectShippingMethod,
  onSaveDeliveryMethods,
  onCancelDeliveryMethod,
  availableShippingMethods,
  showSpinner,
  showDeliveryStep,
  isSaving,
}) => {
  const { locale, formatMessage } = useIntl();
  const { isDeliveryMethodStepError, errorMessage } = useSteppedCheckoutError();

  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart);

  const hasSelectedShippingMethodBeenFalse = selectedShippingMethods?.flatMap(
    shippingMethod => shippingMethod.groups.map(group => group?.key)
  ).some(element => element === undefined);;


  const availableShippingMethodsWithProductQuantityFromLineItems =
    availableShippingMethods?.map((shippingMethod) => ({
      ...shippingMethod,
      groups: shippingMethod.groups.map((shippingMethodGroup) => ({
        ...shippingMethodGroup,
        products: shippingMethodGroup.products.map(
          (shippingMethodGroupProduct) => ({
            ...shippingMethodGroupProduct,
            quantity: cart.lineItems.find(
              (lineItem) =>
                lineItem.variant?.sku === shippingMethodGroupProduct.sku,
            )?.quantity,
          }),
        ),
      })),
    }));
  const skus = availableShippingMethodsWithProductQuantityFromLineItems?.map(
    (shippingMethod) =>
      shippingMethod?.groups?.map((group) =>
        group?.products?.map((product) => product.sku),
      ),
  );

  const availableShippingMethodsWithProductQuantityFromLineItemsLargeProducts =
    availableShippingMethodsWithProductQuantityFromLineItems
      ?.map((shippingMethod) => ({
        ...shippingMethod,
        groups: shippingMethod.groups.filter(
          (group) => group.size === DELIVERY_METHODS_LARGE_PRODUCT,
        ),
      }))
      .filter((shippingMethod) => shippingMethod.groups.length > 0);

  const availableShippingMethodsWithProductQuantityFromLineItemsSmallProducts =
    availableShippingMethodsWithProductQuantityFromLineItems
      ?.map((shippingMethod) => ({
        ...shippingMethod,
        groups: shippingMethod.groups.filter(
          (group) => group.size === DELIVERY_METHODS_SMALL_PRODUCT,
        ),
      }))
      .filter((shippingMethod) => shippingMethod.groups.length > 0);

  const flatSkus = skus.flat();
  const [slaMessage] = useSlaService(
    flatSkus,
    true,
    SLA_PRODUCT_SOURCE.SLA_PRODUCT_SOURCE_PRODUCT,
    true,
  );
  const groupedSelectedShippingMethodsWithProducts =
    getGroupedSelectedShippingMethodsWithProducts({ cart, locale });

  return (
    <>
      {showCompleted && (
        <DeliveryMethodsStepInfo
          shippingMethodsWithProducts={
            groupedSelectedShippingMethodsWithProducts?.shippingMethods
          }
          slaMessage={slaMessage.flatMap((sla) => sla)}
        />
      )}
      {showSpinner && (
        <div className={styles['spinner-container']}>
          <Spinner isModalWrapping={false} theme={THEME_BLOCK_PURPLE} />
        </div>
      )}
      {showDeliveryStep && (
        <div>
          <ErrorBanner
            showError={isDeliveryMethodStepError}
            errorTitle={formatMessage(commonMessages.errorTitle)}
            onCloseErrorMessage={() => dispatch({ type: 'removeError' })}
          >
            {isDeliveryMethodStepError ? formatMessage(errorMessage) : ''}
          </ErrorBanner>
          {availableShippingMethodsWithProductQuantityFromLineItemsLargeProducts.length >
            0 && (
            <div className={styles.title}>
              {formatMessage(messages.largeProductsTitle, {
                quantity:
                  availableShippingMethodsWithProductQuantityFromLineItemsLargeProducts.reduce(
                    (acc, shippingMethod) =>
                      acc +
                      shippingMethod?.groups?.reduce(
                        (groupAcc, group) =>
                          groupAcc +
                          group.products?.reduce(
                            (productsAcc, product) =>
                              productsAcc + product.quantity,
                            0,
                          ),
                        0,
                      ),
                    0,
                  ) ?? 0,
              })}
            </div>
          )}
          {availableShippingMethodsWithProductQuantityFromLineItemsLargeProducts.map(
            (shippingMethod, index) => {
              const currentSlaCard = { ...slaMessage[index] };
              return (
                shippingMethod?.groups.length !== 0 && (
                  <DeliveryMethodCard
                    key={shippingMethod?.key}
                    shipmentTally={formatMessage(messages.shippingMethodTally, {
                      optionNumber: index + 1,
                      total: availableShippingMethods.length,
                    })}
                    selectedShippingMethod={selectedShippingMethods.find(
                      (selectedShippingMethod) =>
                        selectedShippingMethod.parentKey ===
                        shippingMethod?.key,
                    )}
                    shippingMethod={shippingMethod}
                    onSelectShippingMethod={onSelectShippingMethod}
                    slaMessage={currentSlaCard[0]}
                  />
                )
              );
            },
          )}
          {availableShippingMethodsWithProductQuantityFromLineItemsSmallProducts.length >
            0 && (
            <div className={styles.title}>
              {formatMessage(messages.smallProductsTitle, {
                quantity:
                  availableShippingMethodsWithProductQuantityFromLineItemsSmallProducts.reduce(
                    (acc, shippingMethod) =>
                      acc +
                      shippingMethod?.groups?.reduce(
                        (groupAcc, group) =>
                          groupAcc +
                          group.products?.reduce(
                            (productsAcc, product) =>
                              productsAcc + product.quantity,
                            0,
                          ),
                        0,
                      ),
                    0,
                  ) ?? 0,
              })}
            </div>
          )}

          {availableShippingMethodsWithProductQuantityFromLineItemsSmallProducts.map(
            (shippingMethod, index) => {
              const currentSlaCard = { ...slaMessage[index] };
              return (
                shippingMethod?.groups.length !== 0 && (
                  <DeliveryMethodCard
                    key={shippingMethod?.key}
                    shipmentTally={formatMessage(messages.shippingMethodTally, {
                      optionNumber: index + 1,
                      total: availableShippingMethods.length,
                    })}
                    selectedShippingMethod={selectedShippingMethods.find(
                      (selectedShippingMethod) =>
                        selectedShippingMethod.parentKey ===
                        shippingMethod?.key,
                    )}
                    shippingMethod={shippingMethod}
                    onSelectShippingMethod={onSelectShippingMethod}
                    slaMessage={currentSlaCard[0]}
                  />
                )
              );
            },
          )}
          <div className={styles['delivery-buttons-container']}>
            {isCompleted && (
              <div className={styles['button-container']}>
                <SecondaryButton
                  onClick={onCancelDeliveryMethod}
                  isLoading={isSaving}
                  title={formatMessage(messages.cancelButtonLabel)}
                />
              </div>
            )}
            <div className={styles['button-container']}>
              <PrimaryButton
                onClick={() => {
                  onSaveDeliveryMethods({
                    deliveryMethods: selectedShippingMethods,
                  });
                }}
                isLoading={isSaving}
                title={
                  isCompleted
                    ? formatMessage(messages.updateChangesButtonLabel)
                    : formatMessage(messages.nextPaymentInfoButtonLabel)
                }
                isDisabled={hasSelectedShippingMethodBeenFalse}
                size='large'
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

DeliveryMethodsStep.propTypes = {
  showCompleted: PropTypes.bool.isRequired,
  isCompleted: PropTypes.bool,
  selectedShippingMethods: PropTypes.arrayOf(
    PropTypes.shape({
      parentKey: PropTypes.string,
      groups: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          name: PropTypes.string,
          cost: PropTypes.number,
          needsRemoval: PropTypes.bool,
          skus: PropTypes.arrayOf(PropTypes.string),
        }),
      ),
    }),
  ).isRequired,
  onSelectShippingMethod: PropTypes.func.isRequired,
  onSaveDeliveryMethods: PropTypes.func.isRequired,
  onCancelDeliveryMethod: PropTypes.func.isRequired,
  availableShippingMethods: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
      groups: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          products: PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string,
              title: PropTypes.string,
              image: PropTypes.shape({
                url: PropTypes.string,
                label: PropTypes.string,
              }),
              sku: PropTypes.string,
            }),
          ),
          shippingMethods: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
              description: PropTypes.string,
              key: PropTypes.string,
              bussinessDaysToDeliver: PropTypes.string,
              price: PropTypes.shape({
                type: PropTypes.string,
                currencyCode: PropTypes.string,
                centAmount: PropTypes.number,
                fractionDigits: PropTypes.number,
              }),
              offerRemovalOption: PropTypes.bool,
            }),
          ),
        }),
      ),
    }),
  ).isRequired,
  showSpinner: PropTypes.bool.isRequired,
  showDeliveryStep: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
};

DeliveryMethodsStep.defaultProps = {
  isCompleted: false,
};

export default DeliveryMethodsStep;
